import { reactive, readonly, type DeepReadonly } from 'vue'
import type { IconComponent } from '@gat-ship/icons'
import type { AppError } from './AppError'

export type NotificationLevel = 'error' | 'warning' | 'success' | 'info'

export interface NotificationToast {
  id: string
  level: NotificationLevel
  title: string
  description?: string
  icon?: IconComponent
  error?: AppError
  createdAt: number
}

export interface NotificationDialog {
  error: AppError
  title: string
  message: string
  traceId?: string
  forceRefresh: boolean
}

export interface NotificationOptions {
  description?: string
  icon?: IconComponent
}

export interface NotificationSinkState {
  toasts: NotificationToast[]
  dialog: NotificationDialog | null
  history: AppError[]
}

const state = reactive<NotificationSinkState>({
  toasts: [],
  dialog: null,
  history: []
})

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `n_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

function dialogForError(error: AppError): NotificationDialog | null {
  // Dialogs are reserved for fatal server failures. Everything else is a toast.
  const isServerError =
    error.statusCode !== undefined && error.statusCode >= 500
  if (!isServerError) return null

  const detail =
    typeof error.details?.['detail'] === 'string'
      ? error.details['detail']
      : undefined

  return {
    error,
    title: error.message,
    message:
      detail ??
      'An unexpected error occurred. If the issue persists, contact support.',
    traceId: error.traceId,
    forceRefresh: true
  }
}

function pushToast(toast: NotificationToast) {
  state.toasts.push(toast)
}

export interface NotificationSinkApi {
  /**
   * Read-only view of the sink's internal state.
   *
   * Semantics of each field:
   *
   * - **`toasts`** — treat as an **event queue**, not a state container.
   *   The app's `NotificationProvider` subscribes to this array and drains
   *   each toast via `dismissToast` in a microtask after dispatching to the
   *   Toaster component, so in a live app `state.toasts` typically holds
   *   each toast for at most one microtask. Do NOT poll it for "what's
   *   currently visible on screen" — that's the Toaster's own state.
   *   Unit tests can still read it synchronously between `reportError` /
   *   `success` / `info` / `warning` calls because no active subscriber
   *   is draining during synchronous test code.
   *
   * - **`dialog`** — stateful. Persists until `closeDialog()` or until a
   *   subsequent 5xx error overwrites it (newest error wins — see the
   *   `'replaces an existing dialog when a second 5xx error is reported'`
   *   test in `notification-sink.test.ts`).
   *
   * - **`history`** — append-only audit log of every `reportError` call
   *   across the SPA's lifetime. Only `clear()` wipes it.
   */
  readonly state: DeepReadonly<NotificationSinkState>
  reportError(error: AppError): void
  success(title: string, options?: NotificationOptions): void
  info(title: string, options?: NotificationOptions): void
  warning(title: string, options?: NotificationOptions): void
  dismissToast(id: string): void
  closeDialog(): void
  clear(): void
}

/**
 * Single sink for both errors and user-facing notifications (success/info/warning
 * toasts + error dialogs). Decides toast-vs-dialog routing based on error severity
 * so call sites never make that choice.
 *
 * This is a plain reactive singleton — not a Pinia store. Import it anywhere and
 * call `reportError`/`success`/`info`/`warning` without `inject()`.
 */
export const notificationSink: NotificationSinkApi = {
  state: readonly(state),

  /** Report an error. Routes to dialog (5xx / SYSTEM) or toast (other). */
  reportError(error: AppError) {
    state.history.push(error)
    const dialog = dialogForError(error)
    if (dialog) {
      state.dialog = dialog
      return
    }
    pushToast({
      id: newId(),
      level: 'error',
      title: error.message,
      description:
        typeof error.details?.['detail'] === 'string'
          ? error.details['detail']
          : undefined,
      error,
      createdAt: Date.now()
    })
  },

  success(title: string, options?: NotificationOptions) {
    pushToast({
      id: newId(),
      level: 'success',
      title,
      description: options?.description,
      icon: options?.icon,
      createdAt: Date.now()
    })
  },

  info(title: string, options?: NotificationOptions) {
    pushToast({
      id: newId(),
      level: 'info',
      title,
      description: options?.description,
      icon: options?.icon,
      createdAt: Date.now()
    })
  },

  warning(title: string, options?: NotificationOptions) {
    pushToast({
      id: newId(),
      level: 'warning',
      title,
      description: options?.description,
      icon: options?.icon,
      createdAt: Date.now()
    })
  },

  dismissToast(id: string) {
    const idx = state.toasts.findIndex((t) => t.id === id)
    if (idx !== -1) state.toasts.splice(idx, 1)
  },

  closeDialog() {
    state.dialog = null
  },

  /**
   * Full reset — wipes toasts, dialog, AND the `history` audit trail. The
   * only current caller is test `beforeEach`, which relies on this being a
   * full wipe. If you ever need to dismiss the UI without losing history,
   * call `dismissToast` / `closeDialog` individually instead.
   */
  clear() {
    state.toasts.splice(0, state.toasts.length)
    state.dialog = null
    state.history.splice(0, state.history.length)
  }
}

export type NotificationSink = NotificationSinkApi
